import type {
  EmployerFootprintAdminViewDto,
  EmployerVerificationDetail,
  PersistedGateCheckpointDto,
} from '../../api/employerVerificationDashboardApi';
import type { ApplicationJourneyHeaderDto } from '../../api/admin';
import {
  resolvedCheckpointDisplayStatus,
} from '../employerVerificationGateAudit';
import { GATE_ORDER_PIPE } from '../employerVerificationCopy';
import { extractReviewSignalBundle } from '../employment/extractReviewSignalBundle';
import { readRequestSnapshotFromEvidence } from '../employment/diffRequestSnapshots';
import {
  employersLikelyConflict,
  findingTemplateForStep,
  REVIEW_FINDING_TEMPLATES,
  type FindingTemplate,
} from './reviewFindingCopy';
import {
  enrichRoleMismatchFinding,
  readRoleMismatchContext,
} from './roleMismatchFindingPresentation';
import type {
  EmployerConflictInfo,
  EmployerReviewStatus,
  EmployerReviewSummaryViewModel,
  ReviewReason,
  ReviewReasonSeverity,
  ReviewReasonSourceCheck,
} from './reviewReason.types';

const SEVERITY_RANK: Record<ReviewReasonSeverity, number> = {
  BLOCKER: 3,
  CONCERN: 2,
  INFO: 1,
};

function isIssueStatus(status: string): boolean {
  const u = status.trim().toUpperCase();
  return u === 'FAIL' || u === 'REVIEW';
}

function resolveSeverity(
  template: FindingTemplate,
  checks: ReviewReasonSourceCheck[],
): ReviewReasonSeverity {
  const hasHardStopFail = checks.some(
    (c) => c.status === 'FAIL' && c.isHardStop,
  );
  const hasFail = checks.some((c) => c.status === 'FAIL');

  if (hasHardStopFail || (hasFail && template.severity === 'BLOCKER')) {
    return 'BLOCKER';
  }
  if (hasFail || template.severity === 'CONCERN') {
    return 'CONCERN';
  }
  return template.severity;
}

function collectAuditCheckpoints(
  detail: EmployerVerificationDetail | null | undefined,
): ReviewReasonSourceCheck[] {
  const audit = detail?.gateCheckpointAudit;
  if (!audit) return [];

  const collected: ReviewReasonSourceCheck[] = [];
  for (const gate of GATE_ORDER_PIPE) {
    const steps = audit[gate];
    if (!steps?.length) continue;
    for (const step of steps) {
      const status = resolvedCheckpointDisplayStatus(step);
      if (!isIssueStatus(status)) continue;
      collected.push({
        gate,
        step: step.step,
        status,
        isHardStop: step.isHardStop,
      });
    }
  }
  return collected;
}

function buildReasonFromTemplate(
  template: FindingTemplate,
  checks: ReviewReasonSourceCheck[],
): ReviewReason {
  return {
    id: template.id,
    severity: resolveSeverity(template, checks),
    title: template.title,
    explanation: template.explanation,
    category: template.category,
    sourceChecks: checks,
    recommendedAction: template.recommendedAction,
  };
}

function groupChecksIntoReasons(checks: ReviewReasonSourceCheck[]): ReviewReason[] {
  const byTemplate = new Map<string, { template: FindingTemplate; checks: ReviewReasonSourceCheck[] }>();

  for (const check of checks) {
    const template = findingTemplateForStep(check.step);
    if (!template) continue;
    const existing = byTemplate.get(template.id);
    if (existing) {
      const already = existing.checks.some(
        (c) => c.gate === check.gate && c.step === check.step,
      );
      if (!already) existing.checks.push(check);
    } else {
      byTemplate.set(template.id, { template, checks: [check] });
    }
  }

  const reasons: ReviewReason[] = [];
  for (const { template, checks: grouped } of byTemplate.values()) {
    reasons.push(buildReasonFromTemplate(template, grouped));
  }

  return reasons.sort((a, b) => {
    const sev = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (sev !== 0) return sev;
    const aPri = REVIEW_FINDING_TEMPLATES.find((t) => t.id === a.id)?.priority ?? 99;
    const bPri = REVIEW_FINDING_TEMPLATES.find((t) => t.id === b.id)?.priority ?? 99;
    return aPri - bPri;
  });
}

function enrichFindingsWithContext(
  reasons: ReviewReason[],
  input: {
    detail: EmployerVerificationDetail | null;
    footprint: EmployerFootprintAdminViewDto | null | undefined;
    declaredEmployer: string | null;
    declaredJobTitle: string | null;
  },
): ReviewReason[] {
  const roleCtx = readRoleMismatchContext({
    detail: input.detail,
    footprint: input.footprint,
    declaredEmployer: input.declaredEmployer,
    declaredJobTitle: input.declaredJobTitle,
  });
  return reasons.map((reason) => enrichRoleMismatchFinding(reason, roleCtx));
}

function addConflictReason(
  reasons: ReviewReason[],
  conflict: EmployerConflictInfo | null,
): ReviewReason[] {
  if (!conflict) return reasons;
  if (reasons.some((r) => r.id === 'employment-info-conflict')) return reasons;

  const conflictReason: ReviewReason = {
    id: 'employment-info-conflict',
    severity: 'CONCERN',
    title: 'Employment information conflicts across sources',
    explanation: conflict.explanation,
    category: 'EMPLOYER',
    sourceChecks: [],
    recommendedAction: 'Compare declared employment with external signals before confirming.',
  };

  return [...reasons, conflictReason].sort(
    (a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity],
  );
}

function detectConflict(input: {
  declaredEmployer: string | null;
  declaredJobTitle: string | null;
  declaredWorkArrangement: string | null;
  footprint: EmployerFootprintAdminViewDto | null | undefined;
  detail: EmployerVerificationDetail | null | undefined;
}): EmployerConflictInfo | null {
  const declared = input.declaredEmployer?.trim() || null;
  const footprintEmployer = input.footprint?.detectedEmployer?.trim() || null;

  if (employersLikelyConflict(declared, footprintEmployer)) {
    return {
      declaredEmployer: declared,
      declaredJobTitle: input.declaredJobTitle,
      declaredWorkArrangement: input.declaredWorkArrangement,
      externalEmployer: footprintEmployer,
      externalSourceLabel: 'Bank activity signal',
      explanation:
        'The information currently does not provide enough evidence to confirm the declared employment.',
    };
  }

  const bundle = extractReviewSignalBundle({
    evidence: input.detail?.evidence,
  });
  const linkageConflict = bundle?.blockingSignals.find((s) =>
    /different employer|conflict|mismatch|does not match declared/i.test(s.message ?? ''),
  );
  if (linkageConflict && declared) {
    const externalFromMessage = linkageConflict.message?.trim() || 'Different employer signal detected';
    return {
      declaredEmployer: declared,
      declaredJobTitle: input.declaredJobTitle,
      declaredWorkArrangement: input.declaredWorkArrangement,
      externalEmployer: externalFromMessage,
      externalSourceLabel: 'Verification signal',
      explanation:
        'External employment signals do not fully agree with the information provided.',
    };
  }

  return null;
}

function resolveReviewStatus(input: {
  finalStatus: string | null | undefined;
  issueCount: number;
  application?: ApplicationJourneyHeaderDto;
  hasRecords: boolean;
}): { status: EmployerReviewStatus; statusLabel: string } {
  const stored = String(input.finalStatus ?? '').toUpperCase();

  if (/VERIFIED|PASS|SUCCESS|APPROVED/.test(stored) && input.issueCount === 0) {
    return { status: 'verified', statusLabel: 'Verified' };
  }
  if (/REJECT|FAIL|DENIED|NOT.?APPROVED/.test(stored)) {
    return { status: 'not_confirmed', statusLabel: 'Could not confirm' };
  }
  if (/REVIEW|MANUAL|PENDING/.test(stored) || input.issueCount > 0) {
    return { status: 'needs_review', statusLabel: 'Needs review' };
  }
  if (!input.hasRecords) {
    return { status: 'not_started', statusLabel: 'Not started' };
  }
  return { status: 'in_progress', statusLabel: 'In progress' };
}

function buildRecommendedAction(reasons: ReviewReason[]): string | null {
  const blockers = reasons.filter((r) => r.severity === 'BLOCKER');
  const concerns = reasons.filter((r) => r.severity === 'CONCERN');

  const categories = new Set(
    [...blockers, ...concerns].map((r) => r.category),
  );

  if (categories.has('EMPLOYER') && categories.has('INCOME')) {
    return 'Verify the employer and income source before confirming employment.';
  }
  if (categories.has('INCOME')) {
    return 'Review the bank deposits and payroll name against the declared employer.';
  }
  if (categories.has('EMPLOYER')) {
    return 'Confirm the employer is active and matches what the customer declared.';
  }
  if (categories.has('ROLE')) {
    return 'Confirm the job role fits this employer before approving employment.';
  }
  if (categories.has('LOCATION')) {
    return 'Verify work location and employer address with the customer.';
  }
  if (categories.has('TENURE')) {
    return 'Confirm employment start date and supporting documents.';
  }
  if (categories.has('DIGITAL_PRESENCE')) {
    return 'Review business legitimacy signals before confirming employment.';
  }

  const first = blockers[0] ?? concerns[0];
  return first?.recommendedAction ?? null;
}

function readDeclaredWorkArrangement(
  detail: EmployerVerificationDetail | null | undefined,
): string | null {
  const evidence = detail?.evidence;
  if (!evidence || typeof evidence !== 'object') return null;
  const request = (evidence as Record<string, unknown>).requestSnapshot;
  if (!request || typeof request !== 'object') return null;
  const workType = (request as Record<string, unknown>).workType;
  return typeof workType === 'string' ? workType.replace(/_/g, ' ') : null;
}

export function mapEmployerVerificationToReviewSummary(input: {
  detail?: EmployerVerificationDetail | null;
  finalStatus?: string | null;
  declaredEmployer?: string | null;
  declaredJobTitle?: string | null;
  declaredWorkArrangement?: string | null;
  footprint?: EmployerFootprintAdminViewDto | null;
  application?: ApplicationJourneyHeaderDto;
  hasRecords?: boolean;
}): EmployerReviewSummaryViewModel {
  const detail = input.detail ?? null;
  const declaredEmployer =
    input.declaredEmployer?.trim() ||
    detail?.employer?.legalName?.trim() ||
    null;
  const snapshot = readRequestSnapshotFromEvidence(detail?.evidence);
  const declaredJobTitle =
    input.declaredJobTitle?.trim() || snapshot?.jobTitle?.trim() || null;
  const declaredWorkArrangement =
    input.declaredWorkArrangement?.trim() ||
    readDeclaredWorkArrangement(detail) ||
    null;

  const auditChecks = collectAuditCheckpoints(detail);
  let reasons = groupChecksIntoReasons(auditChecks);
  reasons = enrichFindingsWithContext(reasons, {
    detail,
    footprint: input.footprint,
    declaredEmployer,
    declaredJobTitle,
  });

  const conflict = detectConflict({
    declaredEmployer,
    declaredJobTitle,
    declaredWorkArrangement,
    footprint: input.footprint,
    detail,
  });
  reasons = addConflictReason(reasons, conflict);

  const primaryReasons = reasons.filter(
    (r) => r.severity === 'BLOCKER' || r.severity === 'CONCERN',
  );
  const blockerCount = primaryReasons.filter((r) => r.severity === 'BLOCKER').length;
  const concernCount = primaryReasons.filter((r) => r.severity === 'CONCERN').length;
  const issueCount = primaryReasons.length;

  const finalStatus = input.finalStatus ?? detail?.finalStatus ?? null;
  const { status, statusLabel } = resolveReviewStatus({
    finalStatus,
    issueCount,
    application: input.application,
    hasRecords: input.hasRecords ?? Boolean(detail || input.finalStatus),
  });

  const topReasons = primaryReasons.slice(0, 3);
  const additionalReasons = primaryReasons.slice(3);

  const recommendedAction = buildRecommendedAction(reasons);

  const cleanPassMessage =
    status === 'verified'
      ? declaredEmployer
        ? `Employment verified for ${declaredEmployer}.`
        : 'Employment verification completed with no open issues.'
      : status === 'not_started'
        ? 'Employment verification has not started yet.'
        : status === 'in_progress' && issueCount === 0
          ? declaredEmployer
            ? `Verification in progress for ${declaredEmployer}.`
            : 'Employment verification is in progress.'
          : null;

  return {
    status,
    statusLabel,
    issueCount,
    blockerCount,
    concernCount,
    topReasons,
    additionalReasons,
    evidenceFindings: primaryReasons,
    recommendedAction,
    conflict,
    cleanPassMessage,
  };
}

/** Exposed for unit tests — converts raw audit rows without grouping side effects. */
export function issueCheckpointsFromAudit(
  audit: Record<string, PersistedGateCheckpointDto[]> | null | undefined,
): ReviewReasonSourceCheck[] {
  if (!audit) return [];
  const pseudoDetail = { gateCheckpointAudit: audit } as EmployerVerificationDetail;
  return collectAuditCheckpoints(pseudoDetail);
}
